import { ChevronDown } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  AlertBadge,
  Loading,
  ScalrEmptyBox,
  TrendLabel,
} from '@/components/customComponents'
import { Card, CardContent } from '@/components/ui/card'

import { lensMenuCardColors } from '@/constants/COLOR_PALETTE'
import { IAbstractLensItem } from '@/types'
import { formatNumber } from '@/utils'
import { getMetricLabel, getMetricType } from '@/utils/labels'
import ScalrBoxAccordionTable from './ScalrBoxAccordionTable'

interface ScalrBoxAccordionViewProps<T = any> {
  data: Array<IAbstractLensItem<T>>
  loading?: boolean
  onCategoryClick?: (item: IAbstractLensItem<T>) => void
  onItemClick?: (item: IAbstractLensItem<T>, skuItem?: string) => void
  onSubCategoryClick?: (item: IAbstractLensItem<T>) => void // For loading Level 3 data
  selectedItem?: string | null
  getCategoryAlertCount?: (categoryName: string) => {
    total: number
    unread: number
    alerts: any[]
  }
  getSubItemAlertCount?: (subItemName: string) => {
    total: number
    unread: number
    alerts: any[]
  }
  expandedItems?: Record<string, IAbstractLensItem<any>[]> // Level 3 data for expanded items
  isThreeLevel?: boolean // Flag to enable 3-level functionality
}

export default function ScalrBoxAccordionView<T = any>({
  data,
  loading = false,
  onCategoryClick,
  onItemClick,
  onSubCategoryClick,
  selectedItem = null,
  getCategoryAlertCount,
  getSubItemAlertCount,
  expandedItems = {},
  isThreeLevel = false,
}: ScalrBoxAccordionViewProps<T>) {
  const [openItem, setOpenItem] = useState<string | null>(null)

  const colorKeys = Object.keys(lensMenuCardColors)
  const parentColorMap = useMemo(() => {
    const colorMap = new Map<string, string>()
    // Only assign colors to parent-level items (items without parentName)
    // This ensures colors remain stable even when children are loaded
    const parentItems = data.filter((item) => !item.parentName)
    parentItems.forEach((parent, index) => {
      if (parent.item) {
        colorMap.set(parent.item, colorKeys[index % colorKeys.length])
      }
    })
    return colorMap
  }, [data, colorKeys])

  const getItemColor = (itemName: string) => {
    // If this is a parent item, return its assigned color
    if (parentColorMap.has(itemName)) {
      return parentColorMap.get(itemName)!
    }
    // If this is a child item, find its parent and return the parent's color
    const item = data.find((d) => d.item === itemName)
    if (item?.parentName && parentColorMap.has(item.parentName)) {
      return parentColorMap.get(item.parentName)!
    }
    // Fallback to first color
    return colorKeys[0]
  }

  const handleCardClick = (item: IAbstractLensItem<T>) => {
    const itemName = item.item
    const isChild = !!item.parentName

    if (isChild) {
      // Handle child item click - for navigation
      onItemClick?.(item)
    } else {
      // Handle category click - always call onCategoryClick first to load data
      onCategoryClick?.(item)

      // Then handle expanding/collapsing
      if (openItem === itemName) {
        // Close this item if it's already open
        setOpenItem(null)
      } else {
        // Open this item (automatically closes any other open item)
        setOpenItem(itemName || null)
      }
    }
  }

  const isItemSelected = (name: string, isChild: boolean) =>
    selectedItem === name && !isChild

  // Generate mock alert counts for demonstration
  // const getAlertCount = (itemName: string) => {
  //   const alertCounts: Record<string, number> = {
  //     Lattes: 2,
  //     Mochas: 4,
  //     Amped: 4,
  //     Classics: 4,
  //   }
  //   return alertCounts[itemName] || 0
  // }

  return (
    <div className="space-y-4">
      {loading && data.length === 0 ? (
        <Loading isContainer />
      ) : data.length === 0 ? (
        <ScalrEmptyBox />
      ) : (
        <div className="relative">
          <div
            className={`space-y-4 transition-all duration-200 ${
              loading ? 'blur-sm' : ''
            }`}
          >
            {data
              .map((item, index) => {
                const colorKey = getItemColor(item.item || '')
                const colors =
                  lensMenuCardColors[colorKey] || lensMenuCardColors.blue
                const isChild = !!item.parentName
                const isOpen = openItem === item.item

                // Get metric entries for this item
                const metricEntries: Array<{ key: string; value: number }> =
                  item.current
                    ? Object.keys(item.current).map((key) => ({
                        key,
                        value: (item.current as any)[key] as number,
                      }))
                    : []

                // Skip rendering child items here - they will be shown in the expanded section
                if (isChild) {
                  return null
                }

                // Get children for this item
                const childrenItems = data.filter(
                  (d) => d.parentName === item.item
                )

                return (
                  <div key={`${item.item}-${index}`} className="space-y-2">
                    {/* Accordion Card */}
                    <Card
                      className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                        isItemSelected(item.item || '', isChild)
                          ? `border-2 ${colors.border.normal}`
                          : 'border'
                      }`}
                      onClick={() => handleCardClick(item)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-4">
                          {/* Left side - Icon and Title */}
                          <div className="flex items-center gap-3">
                            <div
                              className={`h-10 w-10 rounded-full flex items-center justify-center font-semibold ${colors.icon.bg} ${colors.icon.text}`}
                            >
                              {String(item.item ?? '?').slice(0, 1)}
                            </div>
                            <h3 className="text-xl font-medium">{item.item}</h3>
                          </div>

                          {/* Right side - Alert Count and Expand Icon */}
                          <div className="flex items-center gap-3">
                            {getCategoryAlertCount &&
                              item.item &&
                              (() => {
                                const alertCount = getCategoryAlertCount(
                                  item.item
                                )
                                return alertCount.unread > 0 ? (
                                  <AlertBadge
                                    unreadAlerts={alertCount.unread}
                                  />
                                ) : null
                              })()}
                            <ChevronDown
                              className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                                isOpen ? 'rotate-180' : ''
                              }`}
                            />
                          </div>
                        </div>

                        {/* Dynamic Metrics */}
                        {metricEntries.length > 0 && (
                          <div className="flex items-center justify-between">
                            {metricEntries.map(({ key, value }) => (
                              <div key={key} className="flex items-center">
                                <CardMetric
                                  metricName={getMetricLabel(key as any)}
                                  metricValue={value}
                                  metricType={
                                    getMetricType(key as any) as
                                      | 'money'
                                      | 'percentage'
                                      | 'number'
                                      | 'hour'
                                  }
                                  prevValue={
                                    item.prev
                                      ? (item.prev as any)[key]
                                      : undefined
                                  }
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Expanded Table for Children */}
                    {((isOpen && childrenItems.length > 0) ||
                      (selectedItem === item.item &&
                        childrenItems.length > 0)) && (
                      <div className="ml-4 animate-in slide-in-from-top-2 duration-300">
                        <ScalrBoxAccordionTable
                          key={`${item.item}-${index}`}
                          data={childrenItems}
                          onClick={(itemName, skuItem) => {
                            const childItem = childrenItems.find(
                              (c) => c.item === itemName
                            )
                            if (childItem) {
                              onItemClick?.(childItem, skuItem)
                            }
                          }}
                          onSubCategoryClick={(subCategoryItem) => {
                            onSubCategoryClick?.(subCategoryItem)
                          }}
                          selectedItem={selectedItem}
                          colorKey={colorKey}
                          loading={loading}
                          expandedItems={expandedItems}
                          isThreeLevel={isThreeLevel}
                          getSubItemAlertCount={getSubItemAlertCount}
                        />
                      </div>
                    )}
                  </div>
                )
              })
              .filter(Boolean)}
          </div>
          {loading && <Loading blur />}
        </div>
      )}
    </div>
  )
}

const CardMetric = ({
  metricName,
  metricValue,
  metricType,
  prevValue,
}: {
  metricName: string
  metricValue: string | number
  metricType: 'money' | 'percentage' | 'number' | 'hour'
  prevValue?: string | number
}) => {
  return (
    <div>
      <h2 className="font-medium text-md text-gray-500 tracking-tight">
        {metricName}
      </h2>
      <div className="flex items-center gap-2">
        <p className="font-semibold text-lg tracking-tight">
          {formatNumber(metricValue, metricType)}
        </p>
        {prevValue !== undefined && (
          <TrendLabel
            oldValue={prevValue}
            newValue={metricValue}
            trendType={metricType}
            verySmall={true}
          />
        )}
      </div>
    </div>
  )
}
